import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { HelpTip } from "@/components/HelpTip";
import { Users, CheckCircle2, Rocket, AlertTriangle, CircleDollarSign } from "lucide-react";
import { ACCOUNT_TYPE } from "@/lib/saHealth";

const TILES = [
  ["real_customers", "Real Customers", Users, "text-sky-600 bg-sky-500/10"],
  ["active", "Active", CheckCircle2, "text-emerald-600 bg-emerald-500/10"],
  ["onboarding", "Onboarding", Rocket, "text-amber-600 bg-amber-500/10"],
  ["needs_attention", "Needs Attention", AlertTriangle, "text-rose-600 bg-rose-500/10"],
];

// Primary dashboard strip. Every number comes from /superadmin/overview (same rows the cards render).
export default function PlatformSummary({ data, includeInternal, onToggleInternal, onOpen }) {
  const s = data?.summary || {};
  const rev = s.monthly_revenue || {};
  const revText = Object.keys(rev).length ? Object.entries(rev).map(([c, a]) => `${c} $${Number(a).toLocaleString()}`).join(" · ") : "$0";
  return (
    <section className="space-y-3" data-testid="platform-summary">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Platform at a glance</p>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="include-internal-toggle-label">
          <Switch checked={includeInternal} onCheckedChange={onToggleInternal} data-testid="include-internal-toggle" />
          Include internal test accounts / data
          <HelpTip testid="include-internal-help" text="Internal Live Test Accounts and Sandbox environments are excluded from every customer metric by default. Turn this on to intentionally include them in counts, usage and billing views." />
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {TILES.map(([k, label, Icon, cls]) => (
          <Card key={k} className="p-4 flex items-start justify-between gap-2" data-testid={`ps-${k}`}>
            <div><p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p><p className="font-head text-3xl font-extrabold mt-1" data-testid={`ps-${k}-value`}>{s[k] ?? "-"}</p></div>
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${cls}`}><Icon className="w-5 h-5" /></span>
          </Card>
        ))}
        <Card className="p-4 flex items-start justify-between gap-2" data-testid="ps-monthly_revenue">
          <div><p className="text-[11px] uppercase tracking-wide text-muted-foreground">Monthly Revenue</p><p className="font-head text-xl font-extrabold mt-1.5 break-words" data-testid="ps-monthly_revenue-value">{revText}</p><p className="text-[10px] text-muted-foreground">Active recurring, real customers</p></div>
          <span className="w-9 h-9 rounded-xl flex items-center justify-center text-emerald-600 bg-emerald-500/10"><CircleDollarSign className="w-5 h-5" /></span>
        </Card>
      </div>
      {!includeInternal && (s.internal_test > 0 || s.sandbox > 0) && (
        <p className="text-[11px] text-muted-foreground" data-testid="ps-excluded-note">Excluded from these numbers: {s.internal_test || 0} Internal Live Test account(s), {s.sandbox || 0} Sandbox environment(s).</p>
      )}
      <Card className="p-4" data-testid="ps-attention">
        <h3 className="font-head font-bold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Needs Attention <Badge variant="outline">{data?.attention?.length ?? 0}</Badge></h3>
        {!data?.attention?.length ? <p className="text-sm text-muted-foreground mt-2">Nothing needs attention right now.</p> : (
          <div className="mt-2 space-y-1.5">
            {data.attention.map((a) => (
              <button key={a.id} onClick={() => onOpen(a.id)} className="w-full text-left rounded-lg border border-border border-l-4 border-l-amber-500 px-3 py-2 hover:border-emerald-500/50 flex flex-wrap items-center gap-2" data-testid={`ps-attn-${a.id}`}>
                <span className="font-semibold text-sm">{a.business_name}</span>
                <Badge variant="outline" className="text-[10px]">{a.product_type === "neec_service" ? "NEEC Service" : "NEEC POS"}</Badge>
                {a.account_type !== "customer" && <Badge className={`text-[10px] ${ACCOUNT_TYPE[a.account_type]?.cls}`}>{ACCOUNT_TYPE[a.account_type]?.label}</Badge>}
                <span className="text-xs text-muted-foreground">·</span>
                {a.labels.map((l, i) => <Badge key={i} className={`text-[10px] ${/restricted|Payment required|Past due|Suspended/.test(l) ? "bg-rose-500/15 text-rose-700" : "bg-amber-500/15 text-amber-700"}`} data-testid={`ps-attn-reason-${a.id}-${a.reasons[i]}`}>{l}</Badge>)}
              </button>
            ))}
          </div>
        )}
      </Card>
    </section>
  );
}
